'use client';

import {
    createContext,
    lazy,
    Suspense,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

const TakeoverTransition = lazy(() => import('./toggle/ui/molecules/takeover-transition'));

export type PlayTakeoverOptions = {
    background: TakeoverTransitionBackground;
    logoUrl?: string;
    onCovered: () => void | Promise<void>;
};

export type TakeoverTransitionBackground =
    | { type: 'solid'; color: string }
    | {
          type: 'gradient';
          startColor: string;
          midColor: string;
          endColor: string;
          midPosition: number;
          direction: 'horizontal' | 'vertical';
      };

type Replacement = {
    page: PageWithComponentData;
    regionId: string;
};

type ActiveTransition = PlayTakeoverOptions & { covered: boolean };

type TakeoverContextValue = {
    isTransitioning: boolean;
    replacement: Replacement | null;
    play: (options: PlayTakeoverOptions) => void;
    replacePage: (replacement: Replacement) => void;
    clearReplacement: () => void;
};

const TakeoverContext = createContext<TakeoverContextValue | null>(null);

export const TakeoverProvider = ({ children }: { children: ReactNode }) => {
    const location = useLocation();
    const [locationKey, setLocationKey] = useState(location.key);
    const [transition, setTransition] = useState<ActiveTransition | null>(null);
    const [replacement, setReplacement] = useState<Replacement | null>(null);
    const transitionRef = useRef(transition);
    transitionRef.current = transition;

    if (locationKey !== location.key) {
        setLocationKey(location.key);
        setReplacement(null);
        // A redirect started at the covered hand-off changes the location, but its overlay must finish.
        setTransition((current) => (current?.covered ? current : null));
    }

    const play = useCallback((options: PlayTakeoverOptions) => {
        setTransition((current) => current ?? { ...options, covered: false });
    }, []);

    const replacePage = useCallback((next: Replacement) => setReplacement(next), []);
    const clearReplacement = useCallback(() => setReplacement(null), []);

    const handleCovered = () => {
        // Read at call time: the call can come from a render that predates a navigation.
        const current = transitionRef.current;
        if (!current || current.covered) return;

        setTransition({ ...current, covered: true });
        return current.onCovered();
    };

    const handleFinished = () => setTransition(null);

    const isTransitioning = transition !== null;
    const value = useMemo(
        () => ({ isTransitioning, replacement, play, replacePage, clearReplacement }),
        [clearReplacement, isTransitioning, play, replacePage, replacement]
    );

    return (
        <TakeoverContext.Provider value={value}>
            {children}
            {transition ? (
                <Suspense fallback={null}>
                    <TakeoverTransition
                        background={transition.background}
                        logoUrl={transition.logoUrl}
                        onCovered={handleCovered}
                        onFinished={handleFinished}
                    />
                </Suspense>
            ) : null}
        </TakeoverContext.Provider>
    );
};

// oxlint-disable-next-line react/only-export-components -- the hook and its provider intentionally share this context
export const useTakeover = (): TakeoverContextValue => {
    const context = useContext(TakeoverContext);
    if (!context) {
        throw new Error('useTakeover must be used within TakeoverProvider.');
    }
    return context;
};
